import type { CardFamily } from '../../shared/protocol';

const BONE = '#F5F1E6';
const SIG = '#E8451B';
const MOSS = '#5C7A6E';

/** Topographic card illustrations — contours that encode the mechanic, one or
 *  two signal marks where the mechanic happens (brand card grammar).
 *  v15 BOLT-055: the direction FAMILY resolves per-card (the BOLT-036 title
 *  fix, applied to icons) — Direction keeps the X-cut, Quadrants gets the
 *  cross-split frame, Borders the band-vs-core inset. */
export function CardIcon({ family, id, size = 34 }: { family: CardFamily; id?: string; size?: number }) {
  if (id === 'quadrants')
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <rect x="14" y="14" width="72" height="72" rx="8" stroke={BONE} strokeWidth="1.4" opacity="0.3" />
        <line x1="50" y1="14" x2="50" y2="86" stroke={SIG} strokeWidth="3" strokeLinecap="round" />
        <line x1="14" y1="50" x2="86" y2="50" stroke={SIG} strokeWidth="3" strokeLinecap="round" />
        <rect x="52" y="16" width="32" height="32" rx="5" fill={SIG} opacity="0.28" />
        <circle cx="50" cy="50" r="3" fill={BONE} />
      </svg>
    );
  if (id === 'borders')
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <rect x="12" y="12" width="76" height="76" rx="9" stroke={MOSS} strokeWidth="2.4" />
        <rect x="30" y="30" width="40" height="40" rx="6" stroke={BONE} strokeWidth="1.6" opacity="0.55" />
        <path d="M12 12 h76 v76 h-76 Z M30 30 h40 v40 h-40 Z" fill={MOSS} opacity="0.22" fillRule="evenodd" />
        <circle cx="50" cy="50" r="2.6" fill={BONE} />
      </svg>
    );
  if (family === 'radar')
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="40" stroke={BONE} strokeWidth="1.4" opacity="0.18" />
        <circle cx="50" cy="50" r="28" stroke={BONE} strokeWidth="1.4" opacity="0.32" />
        <circle cx="50" cy="50" r="16" stroke={SIG} strokeWidth="2.4" />
        <line x1="50" y1="50" x2="84" y2="26" stroke={SIG} strokeWidth="3" strokeLinecap="round" />
        <circle cx="84" cy="26" r="3.6" fill={SIG} />
        <circle cx="50" cy="50" r="2.6" fill={BONE} />
      </svg>
    );
  if (family === 'direction')
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="38" stroke={BONE} strokeWidth="1.4" opacity="0.22" />
        <line x1="50" y1="8" x2="50" y2="92" stroke={BONE} strokeWidth="1" opacity="0.25" />
        <line x1="8" y1="50" x2="92" y2="50" stroke={BONE} strokeWidth="1" opacity="0.25" />
        <path d="M50 50 L74 26 A34 34 0 0 1 74 74 Z" fill={SIG} opacity="0.85" />
        <circle cx="50" cy="50" r="3" fill={BONE} />
      </svg>
    );
  if (family === 'airhorn')
    // v16 BOLT-072 — the horn cone + radiating sound arcs (the audible ping).
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <path d="M18 42 L48 30 V70 L18 58 Z" fill={SIG} fillOpacity="0.18" stroke={SIG} strokeWidth="3" strokeLinejoin="round" />
        <rect x="12" y="44" width="8" height="12" rx="2" fill={SIG} />
        <path d="M58 36 Q 68 50, 58 64" stroke={BONE} strokeWidth="2.4" strokeLinecap="round" opacity="0.85" />
        <path d="M68 28 Q 82 50, 68 72" stroke={BONE} strokeWidth="2" strokeLinecap="round" opacity="0.55" />
        <path d="M78 20 Q 96 50, 78 80" stroke={BONE} strokeWidth="1.6" strokeLinecap="round" opacity="0.3" />
      </svg>
    );
  if (family === 'photo')
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <rect x="16" y="30" width="68" height="48" rx="6" stroke={BONE} strokeWidth="1.6" opacity="0.32" />
        <path d="M38 30 l6 -8 h12 l6 8" stroke={BONE} strokeWidth="1.6" opacity="0.32" fill="none" />
        <circle cx="50" cy="55" r="15" stroke={BONE} strokeWidth="1.4" opacity="0.4" />
        <circle cx="50" cy="55" r="9" stroke={SIG} strokeWidth="2.6" />
        <circle cx="50" cy="55" r="2.4" fill={SIG} />
        <circle cx="73" cy="40" r="2.6" fill={SIG} />
      </svg>
    );
  // slice (v21 BOLT-101) — the map (a circle) cut by the LINE you walk; the half
  // holding the hider survives (the filled side). A circle bisected by a chord.
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <circle cx="50" cy="50" r="34" stroke={BONE} strokeWidth="1.6" opacity="0.4" />
      {/* the kept half — the lower-right side of the cut, filled */}
      <path d="M74 26 A34 34 0 0 1 26 74 Z" fill={MOSS} opacity="0.26" />
      {/* the slice line — the walk path, cutting clean across the map */}
      <line x1="18" y1="82" x2="82" y2="18" stroke={SIG} strokeWidth="3" strokeLinecap="round" />
      <circle cx="50" cy="50" r="2.8" fill={SIG} />
    </svg>
  );
}
